import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CreditCard, Coins, Ticket, Check, Wallet } from 'lucide-react';
import { validUntilLabel } from './PromoRewardNotice';
import { getPromotion } from '../../lib/promotions';

/**
 * "How would you like to pay?" — explicit choice at checkout, never applied
 * silently:
 *  - money:    Card / UPI QR / netbanking through Razorpay
 *  - credits:  wallet credits, when the wallet covers this booking (per
 *              /bookings/credit-quote — same rules create_booking enforces)
 *  - day_pass: the customer's Day Pass balance, for Day Pass bookings it covers
 */
const Option = ({ id, selected, disabled, onSelect, icon: Icon, title, detail, note, testId }) => (
  <button
    type="button"
    onClick={() => !disabled && onSelect(id)}
    disabled={disabled}
    aria-pressed={selected}
    data-testid={testId}
    className={`w-full text-left p-4 rounded-lg border transition-all flex items-start gap-3 ${
      selected
        ? 'border-primary bg-primary/5 ring-1 ring-primary'
        : disabled
          ? 'border-border opacity-55 cursor-not-allowed'
          : 'border-border hover:border-primary/50 hover:bg-secondary/40'
    }`}
  >
    <div className={`mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${selected ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
      {selected && <Check className="w-3 h-3 text-primary-foreground" />}
    </div>
    <Icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <p className="font-medium text-sm">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{detail}</p>
      {note && <p className="text-xs mt-1 text-amber-700 dark:text-amber-400">{note}</p>}
    </div>
  </button>
);

const creditsUnavailableReason = (quote, loading) => {
  if (loading) return 'Checking your credit balance…';
  if (!quote) return "Couldn't load your credit balance.";
  if (quote.reason === 'guest') return 'Log in with your Clayworks account to pay with credits.';
  if (quote.reason === 'no_wallet') return "You don't have a credit wallet yet.";
  if (!quote.sufficient) {
    return `Not enough credits — ${Number(quote.credits_required || 0).toFixed(1)} needed, ${Number(quote.available_credits || 0).toFixed(1)} available.`;
  }
  return null;
};

export const PaymentMethodSelector = ({
  paymentMethod, onChange, moneyTotal,
  creditQuote, creditQuoteLoading,
  dayPassApplicable, dayPassBalance, passQuantity = 1,
  promoReward, meetingRoomReward, moneyOnlyNote = null,
}) => {
  const creditsBlocked = creditsUnavailableReason(creditQuote, creditQuoteLoading);
  const offerNote = promoReward?.eligible && promoReward?.freePasses
    ? `The ${promoReward.saleName || 'sale'} offer (${promoReward.freePasses} free Day Passes) applies to money payments only.`
    : meetingRoomReward?.freeHours
      ? `The ${meetingRoomReward.saleName || 'sale'} offer (+${meetingRoomReward.freeHours} hrs free as credits) applies to money payments only.`
      : null;

  return (
    <Card className="bg-card border-border" data-testid="payment-method-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <Wallet className="w-5 h-5 mr-2 text-primary" /> How would you like to pay?
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Option
          id="money" selected={paymentMethod === 'money'} onSelect={onChange}
          icon={CreditCard} testId="pay-with-money"
          title={`Pay with Money · ₹${Number(moneyTotal || 0).toFixed(2)}`}
          detail="Card, or UPI by scanning a QR code — secure checkout by Razorpay."
        />
        {moneyOnlyNote ? (
          <p className="text-xs text-muted-foreground" data-testid="money-only-note">{moneyOnlyNote}</p>
        ) : (
        <>
        <Option
          id="credits" selected={paymentMethod === 'credits'} onSelect={onChange}
          disabled={!!creditsBlocked} icon={Coins} testId="pay-with-credits"
          title={creditQuote?.credits_required != null
            ? `Pay with Credits · ${Number(creditQuote.credits_required).toFixed(1)} credits`
            : 'Pay with Credits'}
          detail={creditsBlocked || `Deducted from your Meeting Room Credits — ${Number(creditQuote.available_credits).toFixed(1)} available. No money charged.`}
          note={!creditsBlocked ? (
            (creditQuote?.promo_credits_available > 0
              ? `Includes ${Number(creditQuote.promo_credits_available).toFixed(1)} bonus credits, used first — valid till ${validUntilLabel(getPromotion()?.reward_valid_until)}. `
              : '') + (offerNote || '')
          ) || null : null}
        />
        {dayPassApplicable && (
          <Option
            id="day_pass" selected={paymentMethod === 'day_pass'} onSelect={onChange}
            icon={Ticket} testId="pay-with-day-pass"
            title={`Use Day Pass balance · ${passQuantity} pass${passQuantity !== 1 ? 'es' : ''}`}
            detail={`From your Day Pass balance — ${dayPassBalance?.total_remaining || 0} remaining. No money charged.`}
            note={offerNote}
          />
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
};
